import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User } from '../types/auth.types';
import { AuthService } from '../services/auth.service';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase.config';
import { FirestoreService } from '../services/firestore.service';
import { NotificationService } from '../services/notification.service';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, userData: Partial<User>) => Promise<User>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const skipNextAuthStateChange = useRef(false);

  useEffect(() => {
    // Timeout de sécurité : si l'authentification prend plus de 10 secondes, on affiche quand même
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ Timeout d\'authentification - affichage forcé de l\'application');
        setLoading(false);
      }
    }, 10000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(timeoutId);

      // Si on vient de faire un signUp, on a déjà les données utilisateur
      // On ignore ce changement d'état pour ne pas écraser avec null (Firestore pas encore accessible)
      if (skipNextAuthStateChange.current) {
        skipNextAuthStateChange.current = false;
        setLoading(false);
        return;
      }

      if (firebaseUser) {
        try {
          const userData = await FirestoreService.getUserById(firebaseUser.uid);
          setUser(userData);
          
          // Enregistrer les notifications uniquement si le profil existe
          if (userData) {
            try {
              if (typeof window !== 'undefined' && 
                  'Notification' in window && 
                  'serviceWorker' in navigator && 
                  'indexedDB' in window) {
                const token = await NotificationService.requestPermission();
                if (token) {
                  await NotificationService.saveToken(firebaseUser.uid, token);
                }
              } else {
                console.log('Navigateur non compatible avec les notifications, fonctionnalité désactivée');
              }
            } catch (notifError) {
              console.error('Erreur lors de la configuration des notifications:', notifError);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [loading]);

  const handleSignUp = async (email: string, password: string, userData: Partial<User>): Promise<User> => {
    const newUser = await AuthService.signUp(email, password, userData);
    skipNextAuthStateChange.current = true;
    setUser(newUser);
    setLoading(false);
    return newUser;
  };

  const value = {
    user,
    loading,
    signIn: AuthService.signIn,
    signOut: AuthService.signOut,
    signUp: handleSignUp,
  };

  // Afficher un indicateur de chargement visible pendant l'initialisation
  if (loading) {
    return (
      <AuthContext.Provider value={value}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#1a1a1a',
          color: '#fff'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '5px solid #333',
            borderTop: '5px solid #fff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '20px', fontSize: '14px', opacity: 0.7 }}>Chargement...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
